import axios from 'axios'
import React, { useEffect, useState, forwardRef } from 'react'
import { createPortal } from 'react-dom'
import { Col, Form, Row, Card, Dropdown, Pagination } from 'react-bootstrap'
import { ArrowLeft } from 'react-bootstrap-icons'
import { useNavigate } from 'react-router-dom'
import Cookies from 'js-cookie'
import { Table, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table'
import {
  BsFunnelFill,
  BsEyeFill,
  BsSearch,
  BsArrowClockwise,
  BsThreeDotsVertical,
  BsCurrencyRupee,
  BsFileEarmarkSpreadsheetFill,
  BsFileEarmarkPdfFill} from 'react-icons/bs'
import {
  LuGraduationCap} from 'react-icons/lu'
import ApiEndpoints, {BASE_URL} from './commonComponents/ApiEndpoints'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import TablePagination from './commonComponents/TablePagination'
import { useInstitute } from './commonComponents/InstituteContext'

/** Renders dropdown menu on document.body so table overflow cannot clip it */
const PortalDropdownMenu = forwardRef(
  ({ children, style, className, show, close, align, ...props }, ref) =>
    createPortal(
      <div ref={ref} style={style} className={className} {...props}>
        {children}
      </div>,
      document.body,
    ),
)
PortalDropdownMenu.displayName = 'PortalDropdownMenu'

const actionMenuPopperConfig = {
  strategy: 'fixed',
  modifiers: [
    {
      name: 'preventOverflow',
      options: {
        boundary: 'viewport',
        padding: 8,
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top-end', 'bottom-end', 'top-start', 'bottom-start'],
        boundary: 'viewport',
        padding: 8,
      },
    },
    {
      name: 'offset',
      options: {
        offset: [0, 8],
      },
    },
  ],
}

export default function DischargedStudent() {
  const { logout, user } = useAuth()
  const { isCollapsed } = useSidebar()
  const navigate = useNavigate()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const instituteId = Cookies.get('institute_id')
  const [logoError, setLogoError] = useState(false)
  const { logo, name, id } = useInstitute();
  const [showFilterCard, setShowFilterCard] = useState(false)
  const [data, setData] = useState([])
  const [filteredData, setFilteredData] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [searchAdmissionNo, setSearchAdmissionNo] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

      const fetchData = async () => {
      try {
        const userCookie = Cookies.get('user')
        const user = JSON.parse(userCookie)
        const token = user?.token

        const response = await axios.get(
          BASE_URL+'/students/inactive',
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'institute-Id': Number(instituteId)}},
        )

        // Safely handle the response
        const fetchedData = response.data?.students || []
        setData(fetchedData)
        setFilteredData(fetchedData)
      } catch (error) {
        console.error(
          error.response?.data?.message || 'Failed to fetch inactive students',
        )
        setData([]) // Fallback to empty array if there's an error
        setFilteredData([]);
      }
    }

  useEffect(() => {
    fetchData()
  }, [])

   useEffect(() => {
    let filtered = data;

    if (searchName.trim() !== "") {
      filtered = filtered.filter((st) =>
        st.name?.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    if (searchAdmissionNo.trim() !== "") {
      filtered = filtered.filter((st) =>
        st.admission_number?.toString().includes(searchAdmissionNo)
      );
    }

    setFilteredData(filtered);
  }, [searchName, searchAdmissionNo, data]);

  // Pagination Logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage)
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem)

  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) {
      setCurrentPage(1)
    }
  }, [filteredData, itemsPerPage, totalPages, currentPage])


  const handleActiveBack = () => {
    navigate('/students')
  }
const handleSeeFee = (studentId) => {
  const selectedStudent = data.find((st) => st.id === studentId);
  if (selectedStudent) {
    navigate("/student/dischargedFees", {
      state: {
        student_id: selectedStudent.id,
        selectedData: selectedStudent.fees || [], // assuming 'fees' key
        studentName: selectedStudent.name}});
  }
};

  const handleView = (id) => {
    if (!id) {
      console.error('Student ID is undefined')
      return
    }
    console.log('Navigate to : ', `/student/discharged/profile/${id}`)
    navigate(`/student/discharged/profile/${id}`)
  }

  const handleRefresh = () => {
    setSearchName("");
    setSearchAdmissionNo("");
    setCurrentPage(1);
    fetchData(); // reload original data
  };

  const canExportStudents =
    user?.user_type === 'admin' || user?.roles?.includes('admin')

  const getLeftStudentExportRows = () =>
    filteredData.map((st) => ({
      'Admission Number': st.admission_number ?? '',
      Name: st.name ?? '',
      'Admission Date': st.admission_date ?? '',
      'Leave Date': st.inactive_date ?? '',
      Course: st.course?.name || st.course_name || 'N/A',
      Phone: st.phone ?? ''}))

  const exportToExcel = () => {
    const exportData = getLeftStudentExportRows()
    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students')
    XLSX.writeFile(workbook, 'Left_Student_Records.xlsx')
  }

  const formatExportDate = (d) => {
    if (!d) return 'N/A'
    try {
      return new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'})
    } catch {
      return String(d)
    }
  }

  const handleSaveAsPDF = () => {
    const rows = getLeftStudentExportRows()
    if (rows.length === 0) return

    try {
      const doc = new jsPDF('l', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const pageHeight = doc.internal.pageSize.getHeight()
      const margin = 10
      const rowHeight = 8
      let yPos = 22

      const headers = [
        'Adm. No.',
        'Name',
        'Adm. Date',
        'Leave Date',
        'Course',
        'Phone',
      ]

      const colWidths = [28, 42, 36, 36, 52, 38]

      const drawTableHeader = () => {
        let xPos = margin
        doc.setFillColor(34, 56, 67)
        doc.rect(margin, yPos, pageWidth - margin * 2, rowHeight, 'F')
        doc.setTextColor(255, 255, 255)
        doc.setFontSize(9)
        doc.setFont('helvetica', 'bold')
        headers.forEach((header, index) => {
          doc.text(header, xPos + 1.5, yPos + 5.5)
          xPos += colWidths[index]
        })
        yPos += rowHeight
      }

      const fitText = (text, maxLength = 18) => {
        const safeText = String(text || '')
        if (safeText.length <= maxLength) return safeText
        return `${safeText.slice(0, maxLength - 3)}...`
      }

      const instituteDisplayName = String(
        name || Cookies.get('name') || Cookies.get('institute_name') || '',
      ).trim()

      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(34, 56, 67)
      doc.text(instituteDisplayName || 'Institute', pageWidth / 2, 10, {
        align: 'center'})

      doc.setFontSize(12)
      doc.text('Left Students', pageWidth / 2, 16, {
        align: 'center'})

      drawTableHeader()

      doc.setFont('helvetica', 'normal')
      doc.setTextColor(17, 24, 39)
      doc.setFontSize(8.5)

      rows.forEach((rowObj, index) => {
        if (yPos + rowHeight > pageHeight - margin) {
          doc.addPage()
          yPos = 14
          drawTableHeader()
          doc.setFont('helvetica', 'normal')
          doc.setTextColor(17, 24, 39)
          doc.setFontSize(8.5)
        }

        let xPos = margin
        const rowData = [
          fitText(rowObj['Admission Number'], 12),
          fitText(rowObj.Name, 22),
          formatExportDate(rowObj['Admission Date']),
          formatExportDate(rowObj['Leave Date']),
          fitText(rowObj.Course, 28),
          fitText(rowObj.Phone, 14),
        ]

        if (index % 2 !== 0) {
          doc.setFillColor(249, 250, 251)
          doc.rect(margin, yPos, pageWidth - margin * 2, rowHeight, 'F')
        }

        rowData.forEach((cell, colIndex) => {
          doc.text(String(cell), xPos + 1.5, yPos + 5.5)
          xPos += colWidths[colIndex]
        })

        yPos += rowHeight
      })

      doc.save('Left_Student_Records.pdf')
    } catch (error) {
      console.error('Error while generating PDF:', error)
    }
  }

  return (
    <>
      <style>
        {`
          .dropdown-toggle-no-caret::after {
            display: none !important;
          }

          /* Allow 3-dots dropdown to render outside table/card */
          .table-card {
            overflow: visible !important;
            position: relative;
          }
          .table-wrapper {
            overflow-x: auto;
            overflow-y: visible !important;
            position: relative;
            max-height: none !important;
          }
          .table,
          .table tbody tr,
          .table td {
            overflow: visible !important;
          }
          .table td {
            position: relative;
          }
          /* Portaled action menus (body) — keep above page chrome */
          body > .dropdown-menu {
            z-index: 2000 !important;
          }
        `}
      </style>
      <div 
        style={{ 
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          marginRight: '0px',
          marginBottom: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px'}}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px'}}
        >
        <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
          <div 
            className="d-flex justify-content-between align-items-center"
            style={{
              background: '#F3F4F6',
              paddingLeft: '28px',
              paddingRight: '28px',
              paddingTop: '12px',
              paddingBottom: '12px',
              overflow: 'visible',
              borderBottom: '1px solid #D1D3D4',
              width: '100%'}}
          >
            {/* Left: Title and Back Button */}
            <div className="d-flex align-items-center gap-3">
              {/* School Logo */}
              <div style={{ 
                width: '40px', 
                height: '40px', 
                borderRadius: '50%', 
                overflow: 'hidden', 
                background: '#223843',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                flexShrink: 0}}>
                {!logoError ? (
                  <img 
                    src={logo} 
                    alt="School Logo" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                )}
              </div>
              
              <h3 
                className="m-0 fw-bold" 
                style={{ 
                  letterSpacing: '-0.02em',
                  overflow: 'visible',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 'clamp(18px, 2.5vw, 22px)',
                  color: '#223843',
                  lineHeight: '1.2',
                  fontFamily: 'Inter, system-ui, sans-serif'}}
              >
                Left Students
              </h3>
              {/* <button
                onClick={handleActiveBack}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: '#223843',
                  border: 'none',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#223843'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title="Go to Active Students"
              >
                <ArrowLeft size={20} />
              </button> */}
            </div>
            {!isMobile && (
              <ProfileDropdown logout={logout} />
            )}
          </div>
        </div>
        </div>
        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px'}}
        >
          {/* Filter Toggle Button and Items Per Page */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              {/* Items Per Page Selector */}
              <div 
                className="d-flex align-items-center"
                style={{
                  backgroundColor: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  gap: '8px',
                  border: '1px solid #D1D3D4'}}
              >
                <span style={{ color: '#6b7280', fontSize: '13px', whiteSpace: 'nowrap', fontFamily: 'Inter, system-ui, sans-serif' }}>
                  Show:
                </span>
                <Form.Select
                  className="w-auto"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(parseInt(e.target.value))
                    setCurrentPage(1)
                  }}
                  style={{
                    borderRadius: '6px',
                    border: '1px solid #D1D3D4',
                    backgroundColor: '#ffffff',
                    color: '#111827',
                    fontSize: '13px',
                    fontWeight: '500',
                    padding: '4px 32px 4px 12px',
                    minWidth: '140px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    height: '36px',
                    fontFamily: 'Inter, system-ui, sans-serif'}}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#223843'
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#D1D3D4'
                  }}
                >
                  <option value="100">100 per page</option>
                  <option value="200">200 per page</option>
                  <option value="300">300 per page</option>
                </Form.Select>
              </div>
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'}}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.color = '#ffffff'
                  e.currentTarget.style.borderColor = showFilterCard ? '#223843' : '#223843'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = showFilterCard ? '#223843' : '#ffffff'
                  e.currentTarget.style.color = showFilterCard ? '#ffffff' : '#223843'
                  e.currentTarget.style.borderColor = '#D1D3D4'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>
            {canExportStudents && filteredData.length > 0 && (
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  onClick={handleSaveAsPDF}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#EF4444',
                    border: 'none',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'}}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#DC2626'
                    e.currentTarget.style.transform = 'scale(1.05)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#EF4444'
                    e.currentTarget.style.transform = 'scale(1)'
                  }}
                  title="Save as PDF"
                >
                  <BsFileEarmarkPdfFill size={20} />
                </button>
                <button
                  type="button"
                  onClick={exportToExcel}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                    border: 'none',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'}}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#059669'
                    e.currentTarget.style.transform = 'scale(1.05)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#10B981'
                    e.currentTarget.style.transform = 'scale(1)'
                  }}
                  title="Export to Excel"
                >
                  <BsFileEarmarkSpreadsheetFill size={20} />
                </button>
              </div>
            )}
          </div>

          {/* Modern Filter Section */}
          <div 
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0'}}
          >
            {showFilterCard && (
              <Card 
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden'}}
              >
                {/* Header */}
                <div 
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6'}}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'}}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5 className="m-0 fw-semibold text-start" style={{ color: '#111827', fontSize: '16px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                          Search & Filter Students
                        </h5>
                        <p className="m-0 mt-1" style={{fontSize: '13px', color: '#6b7280' }}>
                          Find students quickly with advanced filters
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'}}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#223843'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <Row className="g-4 text-start">
                    {/* Filter by Student Name & Admission Number */}
                    <Col xs={12}>
                      <div style={{ 
                        padding: '20px', 
                        backgroundColor: '#F3F4F6', 
                        borderRadius: '8px',
                        border: '1px solid #D1D3D4'}}>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div 
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#10B981',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'}}
                          >
                            <BsSearch style={{ color: '#ffffff', fontSize: '16px' }} />
                          </div>
                          <label className="fw-semibold mb-0" style={{ color: '#111827', fontSize: '14px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                            Filter by Student Name & Admission Number
                          </label>
                        </div>
                        <Row className="g-3">
                          {/* Search Student Name */}
                          <Col xs={12} md={6}>
                            <div style={{ position: 'relative' }}>
                              <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                Search Student Name
                              </label>
                              <div style={{ position: 'relative' }}>
                                <BsSearch 
                                  style={{ 
                                    position: 'absolute', 
                                    left: '12px', 
                                    top: '50%', 
                                    transform: 'translateY(-50%)',
                                    color: '#6b7280',
                                    fontSize: '16px',
                                    zIndex: 1}} 
                                />
                                <Form.Control
                                  type="text"
                                  value={searchName}
                                  onChange={(e) => setSearchName(e.target.value)}
                                  placeholder="Enter student name..."
                                  style={{
                                    paddingLeft: '40px',
                                    borderRadius: '8px',
                                    border: '1px solid #D1D3D4',
                                    fontSize: '14px',
                                    height: '40px',
                                    transition: 'all 0.2s ease',
                                    backgroundColor: '#ffffff',
                                    fontFamily: 'Inter, system-ui, sans-serif'}}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = '#223843'
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = '#D1D3D4'
                                  }}
                                />
                              </div>
                            </div>
                          </Col>

                          {/* Search by Admission Number */}
                          <Col xs={12} md={6}>
                            <div style={{ position: 'relative' }}>
                              <label className="fw-semibold mb-2 d-block" style={{ color: '#6b7280', fontSize: '13px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                Search by Admission Number
                              </label>
                              <div style={{ position: 'relative' }}>
                                <BsSearch 
                                  style={{ 
                                    position: 'absolute', 
                                    left: '12px', 
                                    top: '50%', 
                                    transform: 'translateY(-50%)',
                                    color: '#6b7280',
                                    fontSize: '16px',
                                    zIndex: 1}} 
                                />
                                <Form.Control
                                  type="text"
                                  value={searchAdmissionNo}
                                  onChange={(e) => setSearchAdmissionNo(e.target.value)}
                                  placeholder="Enter admission number..."
                                  style={{
                                    paddingLeft: '40px',
                                    borderRadius: '8px',
                                    border: '1px solid #D1D3D4',
                                    fontSize: '14px',
                                    height: '40px',
                                    transition: 'all 0.2s ease',
                                    backgroundColor: '#ffffff',
                                    fontFamily: 'Inter, system-ui, sans-serif'}}
                                  onFocus={(e) => {
                                    e.target.style.borderColor = '#223843'
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.borderColor = '#D1D3D4'
                                  }}
                                />
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

        {/* Table */}
        <Card className="table-card" style={{ border: '1px solid #D1D3D4', borderRadius: '12px', overflow: 'visible', backgroundColor: '#ffffff' }}>
          <div className="table-wrapper" style={{ borderRadius: '12px' }}>
          <Table className="table mb-0" style={{ borderRadius: '12px', overflow: 'visible' }}>
            <Thead>
              <Tr style={{ backgroundColor: '#F3F4F6' }}>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Admission Number</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Name</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Admission Date</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Leave Date</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Course</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Phone</Th>
                <Th style={{ fontSize: '13px', fontWeight: '600', padding: '16px', border: 'none', borderBottom: '1px solid #D1D3D4', textAlign: 'left', color: '#111827', fontFamily: 'Inter, system-ui, sans-serif' }}>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {Array.isArray(data) && data.length > 0 ? (
                currentItems.map((st, index) => (
                  <Tr 
                    key={st.id}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#F3F4F6',
                      transition: 'background-color 0.2s ease',
                      borderBottom: '1px solid #D1D3D4'}}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#F3F4F6'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#F3F4F6'
                    }}
                  >
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{st.admission_number}</Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{st.name}</Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {st.admission_date
                        ? new Date(st.admission_date).toLocaleDateString(
                            'en-GB',
                            {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'},
                          )
                        : 'N/A'}
                    </Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>
                      {st.inactive_date
                        ? new Date(st.inactive_date).toLocaleDateString(
                            'en-GB',
                            {
                              day: '2-digit',
                              month: 'long',
                              year: 'numeric'},
                          )
                        : 'N/A'}
                    </Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{st.course?.name || st.course_name || 'N/A'}</Td>
                    <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827', textAlign:"left", fontFamily: 'Inter, system-ui, sans-serif' }}>{st.phone || 'N/A'}</Td>
                    <Td className="text-center" style={{ padding: '16px', border: 'none', position: 'relative' }}>
                      {/* Desktop View - Dropdown */}
                      <div className="d-none d-md-block">
                        <Dropdown
                          align="end"
                          drop={
                            currentItems.length === 1
                              ? 'down'
                              : index >= currentItems.length - 2
                                ? 'up'
                                : 'down'
                          }
                        >
                          <Dropdown.Toggle
                            variant="light"
                            id={`dropdown-${st.id}`}
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              backgroundColor: 'transparent',
                              border: '1px solid #D1D3D4',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease'}}
                            className="dropdown-toggle-no-caret"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#223843'
                              e.currentTarget.style.borderColor = '#223843'
                              e.currentTarget.style.color = '#ffffff'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent'
                              e.currentTarget.style.borderColor = '#D1D3D4'
                              e.currentTarget.style.color = '#111827'
                            }}
                          >
                            <BsThreeDotsVertical style={{ color: 'inherit' }} />
                          </Dropdown.Toggle>

                          <Dropdown.Menu
                            as={PortalDropdownMenu}
                            renderOnMount
                            popperConfig={actionMenuPopperConfig}
                            style={{
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              padding: '8px',
                              minWidth: '180px',
                              zIndex: 2000}}
                          >
                            <Dropdown.Item
                              onClick={() => handleView(st.id)}
                              style={{
                                fontSize: '13px',
                                padding: '10px 12px',
                                color: '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s ease',
                                borderRadius: '6px',
                                fontFamily: 'Inter, system-ui, sans-serif'}}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#223843'
                                e.currentTarget.style.color = '#ffffff'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.color = '#111827'
                              }}
                            >
                              <BsEyeFill size={18} /> See Profile
                            </Dropdown.Item>

                            <Dropdown.Item
                              onClick={() => handleSeeFee(st.id)}
                              style={{
                                fontSize: '13px',
                                padding: '10px 12px',
                                color: '#111827',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                transition: 'all 0.2s ease',
                                borderRadius: '6px',
                                fontFamily: 'Inter, system-ui, sans-serif'}}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#10B981'
                                e.currentTarget.style.color = '#ffffff'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.color = '#111827'
                              }}
                            >
                              <BsCurrencyRupee size={18} /> View Fees
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>

                      {/* Mobile View - Circular Buttons in Single Line */}
                      <div className="d-md-none d-flex justify-content-start gap-2">
                        <button
                          onClick={() => handleView(st.id)}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: '#223843',
                            border: 'none',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'}}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#223843'
                            e.currentTarget.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#223843'
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          title="See Profile"
                        >
                          <BsEyeFill size={16} />
                        </button>
                        <button
                          onClick={() => handleSeeFee(st.id)}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            backgroundColor: '#10B981',
                            border: 'none',
                            color: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer'}}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#059669'
                            e.currentTarget.style.transform = 'scale(1.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#10B981'
                            e.currentTarget.style.transform = 'scale(1)'
                          }}
                          title="View Fees"
                        >
                          <BsCurrencyRupee size={16} />
                        </button>
                      </div>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan="7" className="text-center" style={{ padding: '40px', fontSize: '14px', color: '#6b7280', fontFamily: 'Inter, system-ui, sans-serif' }}>
                    No records found
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
          </div>
        </Card>


        {/* Pagination & Total Records */}


                <TablePagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={totalPages}
          indexOfFirstItem={indexOfFirstItem}
          indexOfLastItem={indexOfLastItem}
          totalItems={filteredData.length}
          itemLabel="students"
        />
        </div>
      </div>
    </>
  )
}
